import { useAllAiCreationsCache } from '@/features/my-creations-library/hooks/useAllAiCreations';
import { useCurrentApp } from '@/stores/userDataStore';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { StandaloneMediaAsset, ICardMediaAsset, MediaAssetType } from '@/types/media';
import { deleteMediaAssetById, updateMediaAssetById } from '@/api/media-asset';
import { updatePersonalAvatarById, deletePersonalAvatarById, PersonalAvatarViewData } from '@/api/personal-avatars';
import { Avatar, AvatarType } from '@/types/avatar';
import { useToast } from './useToast';
import { useMemo } from 'react';

const useUpdateMedia = (
  media: ICardMediaAsset | PersonalAvatarViewData | Avatar,
  { onSuccess, onError }: { onSuccess?: () => void; onError?: (asset: string) => void }
) => {
  const { appKey } = useCurrentApp();
  const queryClient = useQueryClient();
  const { invalidateAllAiCreationsQuery } = useAllAiCreationsCache({ appKey });
  const { notify, notifyError } = useToast();
  const isPersonalAvatar = media.type === AvatarType.Personal;

  const asset = useMemo(() => {
    let asset = 'Video';
    switch (media.type) {
      case AvatarType.Personal:
      case AvatarType.BunuPersonal:
      case AvatarType.Default:
        asset = 'Influencer';
        break;
      case MediaAssetType.image:
        asset = 'Image';
        break;
      case MediaAssetType.audio:
        asset = 'Audio';
        break;
      default:
        asset = 'Video';
        break;
    }
    return asset;
  }, [media]);

  const updateMediaFn = (
    name: string
  ): Promise<{ personalAvatar: PersonalAvatarViewData } | { mediaAsset: StandaloneMediaAsset }> => {
    if (isPersonalAvatar) {
      return updatePersonalAvatarById({ appKey, id: media.id, name });
    }
    return updateMediaAssetById({ appKey, id: media.id, name, type: (media as ICardMediaAsset).type! });
  };

  const deleteMediaFn = (): Promise<
    { personalAvatar: PersonalAvatarViewData } | { mediaAsset: StandaloneMediaAsset }
  > => {
    if (isPersonalAvatar) {
      return deletePersonalAvatarById({ appKey, id: media.id });
    }
    return deleteMediaAssetById({ appKey, id: media.id, type: (media as ICardMediaAsset).type! });
  };

  const updateMediaSuccess = () => {
    if (isPersonalAvatar) {
      queryClient.invalidateQueries({ queryKey: ['personal-avatars', appKey] });
      queryClient.invalidateQueries({ queryKey: ['personal-avatar', appKey, media.id] });
    } else {
      invalidateAllAiCreationsQuery();
      queryClient.invalidateQueries({ queryKey: ['video-assets'] });
      queryClient.invalidateQueries({ queryKey: ['image-assets'] });
    }
    onSuccess?.();
  };

  const renameMutation = useMutation({
    mutationFn: updateMediaFn,
    onSuccess: () => {
      notify(`${asset} updated`);
      updateMediaSuccess();
    },
    onError: () => {
      notifyError(`Failed to update ${asset}`);
      onError?.(asset);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteMediaFn,
    onSuccess: () => {
      notify(`${asset} deleted`);
      updateMediaSuccess();
    },
    onError: () => {
      notifyError(`Failed to delete ${asset}`);
      onError?.(asset);
    },
  });

  return { renameMutation, deleteMutation };
};

export default useUpdateMedia;
